"use strict";
/**
 * The Salesforce v2 flow triggers: "A Salesforce Opportunity" (it starts when the
 * Opportunity changes stage, when its name changes, or when a field you choose
 * changes, with optional Filters) and "Someone is added to a Salesforce
 * Campaign" (design-opportunities-steps-campaigns.md, features 1 and 3, and
 * sf-v2-work/opps/plan/plan.md, sections 1.5, 1.7 and 2.5).
 *
 * This file is pure (no database, no network) because three places need the
 * same words and must never disagree about them:
 *  - the flow model (Integration step), which turns a trigger's settings into
 *    the event names its flow listens to;
 *  - the event handler, which gives a trigger's data one stable name for merge
 *    tags;
 *  - the Salesforce sync (packages/api), which emits the events.
 *
 * Event names use Salesforce ids, never names, so a renamed stage or campaign
 * keeps working:
 *
 *   salesforce-opportunity-stage-<OpportunityStage Id>
 *   salesforce-opportunity-field-<field API name in lower case>
 *   salesforce-campaign-member-added-<Campaign Id>
 *   salesforce-campaign-member-status-<Campaign Id>-<CampaignMemberStatus Id>
 *
 * Each change is also announced under a general name ("-any"), the way Stripe's
 * events are (`stripe-any` and `stripe-<type>`). Only the general event is
 * stored on the person's timeline, so one change is one timeline row; the
 * specific one is `storeEvent: false`. The general name is also the one stable
 * key a flow's emails read the trigger's data by (`triggeringEventKey`).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SALESFORCE_TRIGGER_PATHS = exports.describeCampaignTrigger = exports.describeOpportunityTrigger = exports.matchesCampaignMemberEvent = exports.matchesOpportunityEvent = exports.eventNamesForCampaign = exports.eventNamesForOpportunityTrigger = exports.readCampaignConfig = exports.readOpportunityTriggerConfig = exports.startsOnOf = exports.isWhoEnters = exports.WHO_ENTERS = exports.triggeringEventKey = exports.generalEventNameOf = exports.parseSalesforceTriggerEvent = exports.campaignMemberStatusEventName = exports.campaignMemberAddedEventName = exports.opportunityStageEventName = exports.isSalesforceId18 = exports.MEMBER_STATUS_ANY_EVENT = exports.MEMBER_ADDED_ANY_EVENT = exports.opportunityFieldEventName = exports.OPPORTUNITY_FIELD_ANY_EVENT = exports.OPPORTUNITY_STAGE_ANY_EVENT = exports.isSalesforceTriggerOperation = exports.isSalesforceTriggerRefusal = exports.SALESFORCE_TRIGGER_REFUSED = exports.SALESFORCE_CAMPAIGN_MEMBER_OPERATION = exports.SALESFORCE_OPPORTUNITY_OPERATION = exports.SALESFORCE_TRIGGER_SERVICE_ID = void 0;
const opportunityTrigger_1 = require("./opportunityTrigger");
Object.defineProperty(exports, "OPPORTUNITY_FIELD_ANY_EVENT", { enumerable: true, get: function () { return opportunityTrigger_1.OPPORTUNITY_FIELD_ANY_EVENT; } });
Object.defineProperty(exports, "opportunityFieldEventName", { enumerable: true, get: function () { return opportunityTrigger_1.opportunityFieldEventName; } });
const opportunityTriggerFields_1 = require("./opportunityTriggerFields");
const salesforceSyncCriteria_1 = require("../salesforceSyncCriteria");
/** The service these triggers belong to, and the `source` of their events. */
exports.SALESFORCE_TRIGGER_SERVICE_ID = "salesforce";
/** The operation keys of the two triggers, in the Salesforce service definition. */
exports.SALESFORCE_OPPORTUNITY_OPERATION = "/salesforce-opportunity";
exports.SALESFORCE_CAMPAIGN_MEMBER_OPERATION = "/salesforce-campaign-member";
/**
 * The error code of a refusal to switch a Salesforce trigger on: the account is
 * not on the new Salesforce sync, or the trigger's settings cannot be read. It
 * is the person's to put right, so it is a 400 that reaches them as it is,
 * through the layers that turn other failures of switching a flow on into a
 * server error (IntegrationService.setupTriggerStep, Integration.activate).
 */
exports.SALESFORCE_TRIGGER_REFUSED = "salesforce-trigger-refused";
const isSalesforceTriggerRefusal = (error) => {
    const e = error;
    return (typeof e === "object" &&
        e !== null &&
        e.errorCode === exports.SALESFORCE_TRIGGER_REFUSED &&
        e.statusCode === 400);
};
exports.isSalesforceTriggerRefusal = isSalesforceTriggerRefusal;
const isSalesforceTriggerOperation = (operationKey) => operationKey === exports.SALESFORCE_OPPORTUNITY_OPERATION ||
    operationKey === exports.SALESFORCE_CAMPAIGN_MEMBER_OPERATION;
exports.isSalesforceTriggerOperation = isSalesforceTriggerOperation;
// ---------------------------------------------------------------------------
// Event names
const OPPORTUNITY_STAGE_PREFIX = "salesforce-opportunity-stage-";
const MEMBER_ADDED_PREFIX = "salesforce-campaign-member-added-";
const MEMBER_STATUS_PREFIX = "salesforce-campaign-member-status-";
/** Every Opportunity stage change, whatever the stage. */
exports.OPPORTUNITY_STAGE_ANY_EVENT = `${OPPORTUNITY_STAGE_PREFIX}any`;
/** Every campaign member added, whatever the campaign. */
exports.MEMBER_ADDED_ANY_EVENT = `${MEMBER_ADDED_PREFIX}any`;
/** Every campaign member status change, whatever the campaign or status. */
exports.MEMBER_STATUS_ANY_EVENT = `${MEMBER_STATUS_PREFIX}any`;
/**
 * A Salesforce id in the form events and settings carry: 18 letters and digits
 * (the form the REST API answers with, and the form the pickers hand out). A
 * 15 character id is refused rather than guessed at, because the event name
 * must match the emitted one letter for letter.
 */
const ID_18 = /^[A-Za-z0-9]{18}$/;
const isSalesforceId18 = (value) => typeof value === "string" && ID_18.test(value);
exports.isSalesforceId18 = isSalesforceId18;
const requireId = (what, value) => {
    if (!(0, exports.isSalesforceId18)(value)) {
        throw new Error(`Not a Salesforce ${what} id (18 letters and digits)`);
    }
    return value;
};
const opportunityStageEventName = (stageId) => `${OPPORTUNITY_STAGE_PREFIX}${requireId("stage", stageId)}`;
exports.opportunityStageEventName = opportunityStageEventName;
const campaignMemberAddedEventName = (campaignId) => `${MEMBER_ADDED_PREFIX}${requireId("campaign", campaignId)}`;
exports.campaignMemberAddedEventName = campaignMemberAddedEventName;
const campaignMemberStatusEventName = (campaignId, statusId) => `${MEMBER_STATUS_PREFIX}${requireId("campaign", campaignId)}-${requireId("status", statusId)}`;
exports.campaignMemberStatusEventName = campaignMemberStatusEventName;
const ID_PATTERN = "[A-Za-z0-9]{18}";
const OPPORTUNITY_STAGE_NAME = new RegExp(`^${OPPORTUNITY_STAGE_PREFIX}(${ID_PATTERN})$`);
// the part after the prefix is OPPORTUNITY_FIELD_NAME_PART (a spec compares the two)
const OPPORTUNITY_FIELD_NAME = new RegExp(`^${opportunityTrigger_1.OPPORTUNITY_FIELD_EVENT_PREFIX}([a-z][a-z0-9_]{0,79})$`);
const MEMBER_ADDED_NAME = new RegExp(`^${MEMBER_ADDED_PREFIX}(${ID_PATTERN})$`);
const MEMBER_STATUS_NAME = new RegExp(`^${MEMBER_STATUS_PREFIX}(${ID_PATTERN})-(${ID_PATTERN})$`);
/**
 * What an event name says, or undefined when it is not one of these events
 * (any other name, an id of the wrong length, trailing text).
 */
const parseSalesforceTriggerEvent = (name) => {
    if (typeof name !== "string")
        return undefined;
    if (name === exports.OPPORTUNITY_STAGE_ANY_EVENT) {
        return { family: "opportunityStage", general: true };
    }
    if (name === opportunityTrigger_1.OPPORTUNITY_FIELD_ANY_EVENT) {
        return { family: "opportunityField", general: true };
    }
    if (name === exports.MEMBER_ADDED_ANY_EVENT) {
        return { family: "memberAdded", general: true };
    }
    if (name === exports.MEMBER_STATUS_ANY_EVENT) {
        return { family: "memberStatus", general: true };
    }
    const stage = OPPORTUNITY_STAGE_NAME.exec(name);
    if (stage)
        return { family: "opportunityStage", general: false, stageId: stage[1] };
    const field = OPPORTUNITY_FIELD_NAME.exec(name);
    if (field) {
        return { family: "opportunityField", general: false, field: field[1] };
    }
    const added = MEMBER_ADDED_NAME.exec(name);
    if (added) {
        return { family: "memberAdded", general: false, campaignId: added[1] };
    }
    const status = MEMBER_STATUS_NAME.exec(name);
    if (status) {
        return {
            family: "memberStatus",
            general: false,
            campaignId: status[1],
            statusId: status[2],
        };
    }
    return undefined;
};
exports.parseSalesforceTriggerEvent = parseSalesforceTriggerEvent;
/** The general event name of each family. */
const generalEventNameOf = (family) => family === "opportunityStage"
    ? exports.OPPORTUNITY_STAGE_ANY_EVENT
    : family === "opportunityField"
        ? opportunityTrigger_1.OPPORTUNITY_FIELD_ANY_EVENT
        : family === "memberAdded"
            ? exports.MEMBER_ADDED_ANY_EVENT
            : exports.MEMBER_STATUS_ANY_EVENT;
exports.generalEventNameOf = generalEventNameOf;
/**
 * The name a flow's emails read the trigger's data by:
 * `{{ event["salesforce-opportunity-stage-any"].lastCreated.opportunityName }}`.
 *
 * Every one of these events, whatever stage or campaign it names, answers to
 * its general name, so a flow that listens to three stages has one key and the
 * merge tag picker (which lists the general event's schema) shows it. Any other
 * event keeps the key it always had: the name with the prefix an ingested or a
 * Segment event adds taken off.
 *
 * Only a name that IS one of these events is renamed. An event a customer
 * ingests under the same words arrives as `encharge-ingested-event-<name>`, so
 * it is not mistaken for one.
 */
const triggeringEventKey = (eventType) => {
    const parsed = (0, exports.parseSalesforceTriggerEvent)(eventType);
    if (parsed)
        return (0, exports.generalEventNameOf)(parsed.family);
    return eventType
        .replace(/^encharge-ingested-event-/, "")
        .replace(/^segment-integration-/, "");
};
exports.triggeringEventKey = triggeringEventKey;
/**
 * Who enters the flow for an Opportunity:
 *  - contactRoles: the people listed on the Opportunity (its Contact Roles)
 *  - accountPeople: everyone whose MAIN Account is the Opportunity's Account
 *  - primaryContact: the Opportunity's primary Contact Role
 */
exports.WHO_ENTERS = [
    "contactRoles",
    "accountPeople",
    "primaryContact",
];
const isWhoEnters = (value) => exports.WHO_ENTERS.includes(value);
exports.isWhoEnters = isWhoEnters;
const MAX_LABEL = 200;
const MAX_CHOICES = 200;
/** A value as a list: a list, or a list written as JSON text (how a form keeps one). */
const asList = (raw) => {
    if (raw === undefined || raw === null || raw === "")
        return [];
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : undefined;
        }
        catch (e) {
            return undefined;
        }
    }
    return undefined;
};
const labelOf = (raw, fallback) => typeof raw === "string" && raw.trim() !== ""
    ? raw.trim().slice(0, MAX_LABEL)
    : fallback;
const readChoices = (raw, what, problems, withCampaign = false) => {
    const list = asList(raw);
    if (!list) {
        problems.push(`The ${what} list could not be read`);
        return [];
    }
    if (list.length > MAX_CHOICES) {
        problems.push(`Choose at most ${MAX_CHOICES} ${what}s`);
        return [];
    }
    const seen = new Set();
    const choices = [];
    list.forEach((item) => {
        const entry = item;
        const id = entry && typeof entry === "object" ? entry.id : undefined;
        if (!(0, exports.isSalesforceId18)(id)) {
            problems.push(`A ${what} was not chosen from the list (it has no Salesforce id)`);
            return;
        }
        const campaignId = withCampaign ? entry === null || entry === void 0 ? void 0 : entry.campaignId : undefined;
        if (withCampaign && !(0, exports.isSalesforceId18)(campaignId)) {
            problems.push(`The ${what} "${labelOf(entry === null || entry === void 0 ? void 0 : entry.label, id)}" has no campaign`);
            return;
        }
        const key = withCampaign ? `${campaignId}-${id}` : id;
        if (seen.has(key))
            return;
        seen.add(key);
        choices.push(Object.assign({ id, label: labelOf(entry === null || entry === void 0 ? void 0 : entry.label, id) }, (withCampaign ? { campaignId: campaignId } : {})));
    });
    return choices;
};
/** What starts the flow: the config's `startsOn`, or "stage" when it has none. */
const startsOnOf = (config) => { var _a; return (_a = config.startsOn) !== null && _a !== void 0 ? _a : "stage"; };
exports.startsOnOf = startsOnOf;
/** An object kept as an object, or as JSON text (how a form keeps one); anything else is nothing. */
const asRecord = (raw) => {
    let value = raw;
    if (typeof value === "string") {
        try {
            value = JSON.parse(value);
        }
        catch (e) {
            return undefined;
        }
    }
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? value
        : undefined;
};
/** Words for the first thing wrong with a Filters value (the criteria helper's problem codes). */
const filterProblemText = (problem, path) => {
    const where = path ? ` (${path})` : "";
    switch (problem) {
        case "overRules":
        case "overTotal":
        case "overKeepOut":
        case "overGroups":
            return `The Filters have too many rules or groups${where}.`;
        case "tooBig":
            return "The Filters are too big.";
        default:
            return `The Filters cannot be read${where}: ${problem}.`;
    }
};
/** Whether a rule that needs a value has one: text, a list, or a day (the loader checks the day). */
const ruleHasItsValue = (rule) => {
    if ((0, salesforceSyncCriteria_1.takesNoValue)(rule.condition))
        return true;
    if ((0, salesforceSyncCriteria_1.takesValueList)(rule.condition)) {
        return (Array.isArray(rule.propertyValues) &&
            rule.propertyValues.length > 0 &&
            rule.propertyValues.every((item) => typeof item === "string" && item !== ""));
    }
    if ((0, salesforceSyncCriteria_1.takesDate)(rule.condition))
        return true;
    return typeof rule.propertyValue === "string" && rule.propertyValue !== "";
};
/**
 * Checks the Filters of a flow's trigger: the rules are readable by the sync's
 * own rule engine, each rule has the value it needs, at most
 * OPPORTUNITY_FILTER_MAX_RULES together, and every field is one a Filter can
 * read. `undefined` and empty rules mean no Filters.
 */
const readFilters = (raw, problems) => {
    if (raw === undefined || raw === null || raw === "")
        return undefined;
    const filters = asRecord(raw);
    const rules = filters === null || filters === void 0 ? void 0 : filters.rules;
    if (!filters || rules === undefined || rules === null) {
        // `{}` and `{ rules: null }` are what a form that was emptied leaves
        if (filters && Object.keys(filters).length <= 1)
            return undefined;
        problems.push("The Filters could not be read.");
        return undefined;
    }
    // the loader's check: rules the rule engine can read (a known word, a field
    // name it can select, a real day, a number). Not the save check of the Lead
    // and Contact rules, which refuses a dotted field name: a Filter may read the
    // owner's name and the Account's fields
    const readable = (0, salesforceSyncCriteria_1.readableOrWhy)({ rules });
    if (!readable.ok) {
        problems.push(filterProblemText(readable.problem, readable.path));
        return undefined;
    }
    const side = (0, salesforceSyncCriteria_1.readSideOf)({ rules });
    const all = [...side.groups.flat(), ...side.keepOut];
    if (all.length === 0)
        return undefined;
    if (all.length > opportunityTriggerFields_1.OPPORTUNITY_FILTER_MAX_RULES ||
        side.groups.length > salesforceSyncCriteria_1.LIMITS.groups) {
        problems.push(`Use at most ${opportunityTriggerFields_1.OPPORTUNITY_FILTER_MAX_RULES} rules in the Filters.`);
        return undefined;
    }
    // a rule that needs a value has one (the loader does not ask; the flow must)
    const empty = all.find((rule) => !ruleHasItsValue(rule));
    if (empty) {
        problems.push(`The Filters rule on ${String(empty.propertyName)} needs a value to compare with.`);
        return undefined;
    }
    const bad = all.find((rule) => { var _a; return !(0, opportunityTriggerFields_1.opportunityFieldCanFilter)(String((_a = rule === null || rule === void 0 ? void 0 : rule.propertyName) !== null && _a !== void 0 ? _a : "")); });
    if (bad) {
        problems.push(`${String(bad.propertyName)} cannot be used in the Filters. Use a field of the Opportunity, the Owner's name, or a field of the Account.`);
        return undefined;
    }
    return { rules: rules };
};
/**
 * Read the settings of the Opportunity trigger from the step's input values.
 * Anything unusable is named in `problems`; nothing is guessed (an id that is
 * not a Salesforce id would make an event nobody emits, and the flow would wait
 * for ever).
 *
 * `startsOn` absent, null or empty reads as "stage": every step saved before
 * the Name and Field starts existed reads as before. The settings of another
 * start are not read (a form that changed its choice leaves the old settings
 * behind), so `stages` and `only` are for the Stage start, `field` for the
 * Field start.
 */
const readOpportunityTriggerConfig = (raw) => {
    const values = (raw && typeof raw === "object" ? raw : {});
    const problems = [];
    let startsOn = "stage";
    if (values.startsOn !== undefined &&
        values.startsOn !== null &&
        values.startsOn !== "") {
        if ((0, opportunityTrigger_1.isOpportunityStartsOn)(values.startsOn)) {
            startsOn = values.startsOn;
        }
        else {
            problems.push("Choose what starts the flow: Stage, Opportunity Name or Opportunity Field");
        }
    }
    let stages = [];
    let only;
    if (startsOn === "stage") {
        stages = readChoices(values.stages, "stage", problems);
        if (values.only === "won" || values.only === "lost") {
            only = values.only;
        }
        else if (values.only !== undefined &&
            values.only !== null &&
            values.only !== "" &&
            values.only !== "any") {
            problems.push('"Only" must be won or lost');
        }
    }
    let field;
    if (startsOn === "field") {
        const chosen = asRecord(values.field);
        const name = chosen === null || chosen === void 0 ? void 0 : chosen.name;
        const why = (0, opportunityTriggerFields_1.whyOpportunityFieldCannotStart)(name);
        if (why) {
            problems.push(why);
        }
        else {
            field = {
                name: name,
                label: labelOf(chosen === null || chosen === void 0 ? void 0 : chosen.label, name),
            };
        }
    }
    const filters = readFilters(values.filters, problems);
    if (!(0, exports.isWhoEnters)(values.whoEnters)) {
        problems.push("Choose who enters the flow");
    }
    if (problems.length > 0)
        return { ok: false, problems };
    return {
        ok: true,
        config: Object.assign(Object.assign(Object.assign(Object.assign({ startsOn,
            stages }, (only ? { only } : {})), (field ? { field } : {})), (filters ? { filters } : {})), { whoEnters: values.whoEnters }),
    };
};
exports.readOpportunityTriggerConfig = readOpportunityTriggerConfig;
const readCampaignConfig = (raw) => {
    const values = (raw && typeof raw === "object" ? raw : {});
    const problems = [];
    const campaigns = readChoices(values.campaigns, "campaign", problems);
    if (campaigns.length === 0 && problems.length === 0) {
        problems.push("Choose at least one campaign");
    }
    let when = "added";
    if (values.when === "statusBecomes") {
        when = "statusBecomes";
    }
    else if (values.when !== undefined &&
        values.when !== null &&
        values.when !== "" &&
        values.when !== "added") {
        problems.push('"When" must be added or statusBecomes');
    }
    // statuses only matter for "status becomes"; leftovers from an earlier
    // choice of "added" are not read
    const statuses = when === "statusBecomes"
        ? readChoices(values.statuses, "member status", problems, true)
        : [];
    if (when === "statusBecomes") {
        if (statuses.length === 0 && problems.length === 0) {
            problems.push("Choose at least one member status");
        }
        const chosen = new Set(campaigns.map((campaign) => campaign.id));
        statuses.forEach((status) => {
            if (!chosen.has(status.campaignId)) {
                problems.push(`The status "${status.label}" belongs to a campaign that is not chosen`);
            }
        });
    }
    if (problems.length > 0)
        return { ok: false, problems };
    return {
        ok: true,
        config: { campaigns, when, statuses },
    };
};
exports.readCampaignConfig = readCampaignConfig;
/**
 * The event names an Opportunity trigger listens to:
 *  - Stage: one for each chosen stage, or the general one for any stage. A "won
 *    only" or "lost only" choice is checked when the event arrives
 *    (matchesOpportunityEvent), not in the name.
 *  - Opportunity Name: salesforce-opportunity-field-name.
 *  - Opportunity Field: salesforce-opportunity-field-<the field in lower case>.
 */
const eventNamesForOpportunityTrigger = (config) => {
    const startsOn = (0, exports.startsOnOf)(config);
    if (startsOn === "name")
        return [(0, opportunityTrigger_1.opportunityFieldEventName)("Name")];
    if (startsOn === "field") {
        return config.field ? [(0, opportunityTrigger_1.opportunityFieldEventName)(config.field.name)] : [];
    }
    return config.stages.length === 0
        ? [exports.OPPORTUNITY_STAGE_ANY_EVENT]
        : config.stages.map((stage) => (0, exports.opportunityStageEventName)(stage.id));
};
exports.eventNamesForOpportunityTrigger = eventNamesForOpportunityTrigger;
/**
 * The event names a campaign trigger listens to: "added" is one for each
 * campaign, "status becomes" is one for each chosen status of a campaign.
 */
const eventNamesForCampaign = (config) => config.when === "added"
    ? config.campaigns.map((campaign) => (0, exports.campaignMemberAddedEventName)(campaign.id))
    : config.statuses.map((status) => (0, exports.campaignMemberStatusEventName)(status.campaignId, status.id));
exports.eventNamesForCampaign = eventNamesForCampaign;
const asOpportunity = (payload) => payload && typeof payload === "object"
    ? payload
    : {};
/**
 * Whether a person this Opportunity event names enters this trigger's flow. The
 * event name already picked the stage or the field; this checks the rest of the
 * settings, so a flow never starts on a mismatch even if an event reaches it by
 * mistake:
 *  - the person is covered by the "who enters" choice of this flow
 *  - stage start: the stage is one of the chosen ones (when some are chosen);
 *    won only: the Opportunity is won; lost only: it is closed and not won
 *  - name or field start: the event is a change of that field
 * The Filters were judged where the change was made (they need Salesforce's
 * values, which are not all in the event) and the event handler left the flows
 * that did not pass out (`forSteps`).
 */
const matchesOpportunityEvent = (config, payload) => {
    var _a;
    const data = asOpportunity(payload);
    if (!Array.isArray(data.enteredVia) ||
        !data.enteredVia.includes(config.whoEnters)) {
        return false;
    }
    const startsOn = (0, exports.startsOnOf)(config);
    if (startsOn !== "stage") {
        const wanted = startsOn === "name" ? "Name" : (_a = config.field) === null || _a === void 0 ? void 0 : _a.name;
        return (typeof wanted === "string" &&
            typeof data.field === "string" &&
            data.field.toLowerCase() === wanted.toLowerCase());
    }
    // a stage flow starts on a stage change, never on a change of a field
    if (typeof data.field === "string")
        return false;
    if (config.stages.length > 0 &&
        !config.stages.some((stage) => stage.id === data.stageId)) {
        return false;
    }
    if (config.only === "won")
        return data.isWon === true;
    if (config.only === "lost")
        return data.isClosed === true && data.isWon === false;
    return true;
};
exports.matchesOpportunityEvent = matchesOpportunityEvent;
/** Whether a campaign member event is one this trigger's flow starts on. */
const matchesCampaignMemberEvent = (config, payload) => {
    const data = (payload && typeof payload === "object"
        ? payload
        : {});
    if (config.when === "added") {
        return (data.change === "added" &&
            config.campaigns.some((campaign) => campaign.id === data.campaignId));
    }
    return (data.change === "status" &&
        config.statuses.some((status) => status.campaignId === data.campaignId && status.id === data.statusId));
};
exports.matchesCampaignMemberEvent = matchesCampaignMemberEvent;
// ---------------------------------------------------------------------------
// Card text
/** "A", "A or B", "A, B or C" */
const orList = (items) => items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;
const WHO_TEXT = {
    contactRoles: "for the people on the Opportunity",
    accountPeople: "for everyone at its Account",
    primaryContact: "for its main contact",
};
/**
 * The sentence on the trigger's card, for example "When a Salesforce Opportunity
 * moves to Closed Won, for everyone at its Account", "When a Salesforce
 * Opportunity's Amount changes, for the people on the Opportunity" or "When a
 * Salesforce Opportunity's name changes (with Filters), for its main contact".
 * The stage's and the field's labels are stored with their ids and names, so the
 * card can show them without asking Salesforce.
 */
const describeOpportunityTrigger = (config) => {
    var _a, _b;
    const startsOn = (0, exports.startsOnOf)(config);
    const extras = [
        ...(startsOn === "stage" && config.only
            ? [`${config.only} Opportunities only`]
            : []),
        ...(config.filters ? ["with Filters"] : []),
    ];
    const tail = extras.length > 0 ? ` (${extras.join(", ")})` : "";
    let what;
    if (startsOn === "name") {
        what = "'s name changes";
    }
    else if (startsOn === "field") {
        what = `'s ${((_a = config.field) === null || _a === void 0 ? void 0 : _a.label) || ((_b = config.field) === null || _b === void 0 ? void 0 : _b.name) || "field"} changes`;
    }
    else {
        const labels = config.stages.map((stage) => stage.label);
        what =
            labels.length === 0 ? " changes stage" : ` moves to ${orList(labels)}`;
    }
    return `When a Salesforce Opportunity${what}${tail}, ${WHO_TEXT[config.whoEnters]}`;
};
exports.describeOpportunityTrigger = describeOpportunityTrigger;
const quoted = (labels) => labels.map((label) => `'${label}'`);
const describeCampaignTrigger = (config) => {
    const campaigns = quoted(config.campaigns.map((campaign) => campaign.label));
    const named = `${campaigns.length === 1 ? "campaign" : "campaigns"} ${orList(campaigns)}`;
    if (config.when === "added") {
        return `When someone is added to the Salesforce ${named}`;
    }
    const statuses = quoted(config.statuses.map((status) => status.label));
    return `When a member's status becomes ${orList(statuses)} in the Salesforce ${named}`;
};
exports.describeCampaignTrigger = describeCampaignTrigger;
// ---------------------------------------------------------------------------
// The operations, as the Salesforce service definition lists them
const PICKER_ITEM = {
    type: "object",
    required: ["id", "label"],
    properties: {
        id: { type: "string", title: "Salesforce id" },
        label: { type: "string", title: "Name" },
    },
};
const emptyResponse = {
    default: {
        description: "",
        content: {
            "application/json": {
                schema: {
                    type: "array",
                    items: {
                        type: "object",
                        required: ["id"],
                        properties: {
                            id: {
                                type: "string",
                                title: "ID",
                                description: "Person ID",
                                endUserField: "id",
                                "x-encharge-ui": { "ui:widget": "hidden" },
                            },
                        },
                    },
                },
            },
        },
    },
};
/**
 * The two operations as OpenAPI path items, to be listed in the Salesforce
 * service definition (the `services` row, which packages/service-definitions
 * installs on every deploy and adds these to, read from the built package).
 * They have no `events` entry on purpose: the flow model builds the
 * event names itself from the settings (Integration.getEventTriggers), so no
 * call to a Salesforce service is made to register a flow. They run inside
 * Encharge too (Service.performOperation), because everything they need is in
 * the event.
 *
 * The pickers (stage, campaign, status) are the editor's: they fill `stages`,
 * `campaigns` and `statuses` with {id, label} choices from the picker routes
 * (packages/api, /v1/salesforce-triggers).
 */
exports.SALESFORCE_TRIGGER_PATHS = {
    [exports.SALESFORCE_OPPORTUNITY_OPERATION]: {
        post: {
            summary: "Salesforce Opportunity changes",
            description: "Starts when a Salesforce Opportunity moves to a stage, when its name changes, or when a field you choose changes. Starts within about 2 minutes of the change in Salesforce.",
            "x-encharge-operation": {
                type: "trigger",
                triggerType: "event",
                producesEndUsers: true,
                resultsAlreadyMappedToEndUsers: true,
                neededEndUserFields: ["id"],
            },
            requestBody: {
                content: {
                    "application/json": {
                        schema: {
                            type: "object",
                            required: ["whoEnters"],
                            properties: {
                                startsOn: {
                                    type: "string",
                                    title: "What starts it",
                                    default: "stage",
                                    oneOf: [
                                        { enum: ["stage"], title: "Stage" },
                                        { enum: ["name"], title: "Opportunity Name" },
                                        { enum: ["field"], title: "Opportunity Field" },
                                    ],
                                    "x-encharge-ui": { "ui:widget": "radio" },
                                },
                                stages: {
                                    type: "array",
                                    title: "To stage",
                                    description: "Leave empty for any stage.",
                                    items: PICKER_ITEM,
                                },
                                only: {
                                    type: "string",
                                    title: "Only",
                                    default: "any",
                                    oneOf: [
                                        { enum: ["any"], title: "Won and lost Opportunities" },
                                        { enum: ["won"], title: "Only won Opportunities" },
                                        { enum: ["lost"], title: "Only lost Opportunities" },
                                    ],
                                    "x-encharge-ui": { "ui:widget": "radio" },
                                },
                                field: {
                                    type: "object",
                                    title: "Opportunity field",
                                    description: "The field whose change starts the flow. Only for Opportunity Field.",
                                    required: ["name", "label"],
                                    properties: {
                                        name: { type: "string", title: "API name" },
                                        label: { type: "string", title: "Name" },
                                    },
                                },
                                // `{ rules: <the rule groups> }`, kept whole: the form schema
                                // (FieldsSchema) keeps an object with free keys under
                                // additionalProperties, and drops the nested keys of one
                                // that lists `properties`
                                filters: {
                                    type: "object",
                                    title: "Filters",
                                    description: "Optional rules on the Opportunity. The flow starts only when they are true. Text ignores capital letters.",
                                    additionalProperties: { type: "object" },
                                },
                                whoEnters: {
                                    type: "string",
                                    title: "Who enters the flow",
                                    oneOf: [
                                        {
                                            enum: ["contactRoles"],
                                            title: "The people on the Opportunity (its Contact Roles)",
                                        },
                                        {
                                            enum: ["accountPeople"],
                                            title: "Everyone at the Opportunity's Account (people whose main Account it is)",
                                        },
                                        {
                                            enum: ["primaryContact"],
                                            title: "The Opportunity's main contact (its primary Contact Role)",
                                        },
                                    ],
                                    "x-encharge-ui": { "ui:widget": "radio" },
                                },
                            },
                        },
                    },
                },
            },
            responses: emptyResponse,
        },
    },
    [exports.SALESFORCE_CAMPAIGN_MEMBER_OPERATION]: {
        post: {
            summary: "Added to a Salesforce Campaign",
            description: "Starts within about 10 minutes of the change in Salesforce.",
            "x-encharge-operation": {
                type: "trigger",
                triggerType: "event",
                producesEndUsers: true,
                resultsAlreadyMappedToEndUsers: true,
                neededEndUserFields: ["id"],
            },
            requestBody: {
                content: {
                    "application/json": {
                        schema: {
                            type: "object",
                            required: ["campaigns"],
                            properties: {
                                campaigns: {
                                    type: "array",
                                    title: "Campaigns",
                                    items: PICKER_ITEM,
                                },
                                when: {
                                    type: "string",
                                    title: "When",
                                    default: "added",
                                    oneOf: [
                                        { enum: ["added"], title: "Someone is added" },
                                        {
                                            enum: ["statusBecomes"],
                                            title: "A member's status becomes",
                                        },
                                    ],
                                    "x-encharge-ui": { "ui:widget": "radio" },
                                },
                                statuses: {
                                    type: "array",
                                    title: "Member statuses",
                                    items: Object.assign(Object.assign({}, PICKER_ITEM), { required: ["id", "label", "campaignId"], properties: Object.assign(Object.assign({}, PICKER_ITEM.properties), { campaignId: { type: "string", title: "Campaign id" } }) }),
                                },
                            },
                        },
                    },
                },
            },
            responses: emptyResponse,
        },
    },
};
//# sourceMappingURL=salesforceTriggers.js.map